import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, Loader2, CheckCircle2, Car, Wrench, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function ReviewPage() {
  const { token } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | form | done | already | invalid
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/review-request/${token}`)
      .then(({ data }) => {
        setInfo(data);
        setState(data.submitted ? "already" : "form");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async () => {
    if (rating < 1) { toast.error(t("review.pick_rating")); return; }
    setSending(true);
    try {
      await api.post(`/review-request/${token}`, { rating, text });
      setState("done");
    } catch (e) {
      if (e.response?.status === 409) setState("already");
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const serviceLabel = info ? (t(`services.${info.service_id}.label`) !== `services.${info.service_id}.label` ? t(`services.${info.service_id}.label`) : info.service_label) : "";

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen flex items-center justify-center px-5 grid-bg py-16" data-testid="review-page">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6"><button onClick={() => navigate("/")}><Logo height="h-11" /></button></div>
        <div className="flex justify-center items-center gap-3 mb-8"><LanguageSwitcher /><ThemeToggle /></div>

        <div className="border border-white/10 bg-[#0C0C0E] p-8 sm:p-10">
          {state === "loading" && (
            <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          )}

          {state === "invalid" && (
            <div className="text-center py-8" data-testid="review-invalid">
              <h1 className="font-display font-900 text-white text-2xl tracking-tight uppercase mb-3">{t("review.invalid_title")}</h1>
              <p className="text-zinc-500 text-sm mb-8">{t("review.invalid_desc")}</p>
              <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm"><HomeIcon className="w-4 h-4" /> {t("review.home")}</button>
            </div>
          )}

          {state === "already" && (
            <div className="text-center py-8" data-testid="review-already">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h1 className="font-display font-900 text-white text-2xl tracking-tight uppercase mb-3">{t("review.already_title")}</h1>
              <p className="text-zinc-500 text-sm mb-8">{t("review.already_desc")}</p>
              <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm"><HomeIcon className="w-4 h-4" /> {t("review.home")}</button>
            </div>
          )}

          {state === "done" && (
            <div className="text-center py-8" data-testid="review-done">
              <div className="flex justify-center gap-1 mb-4">{[1,2,3,4,5].map((i) => <Star key={i} className={`w-7 h-7 ${i <= rating ? "text-[#FFB800] fill-[#FFB800]" : "text-zinc-700"}`} />)}</div>
              <h1 className="font-display font-900 text-white text-2xl tracking-tight uppercase mb-3">{t("review.thanks_title")}</h1>
              <p className="text-zinc-500 text-sm mb-8">{t("review.thanks_desc")}</p>
              <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm"><HomeIcon className="w-4 h-4" /> {t("review.home")}</button>
            </div>
          )}

          {state === "form" && info && (
            <div data-testid="review-form">
              <h1 className="font-display font-900 text-white text-2xl tracking-tight uppercase mb-1">{t("review.title")}</h1>
              <p className="text-zinc-500 text-sm mb-6">{t("review.sub")}</p>

              <div className="border border-white/10 bg-[#050505] p-4 mb-8 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-400"><Wrench className="w-4 h-4 text-[#00E5FF]" /> {serviceLabel}</div>
                {info.vehicle?.marque && <div className="flex items-center gap-2 text-zinc-500"><Car className="w-4 h-4 text-[#E60000]" /> {info.vehicle.marque} {info.vehicle.modele}</div>}
              </div>

              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-3">{t("review.rating_label")}</label>
              <div className="flex items-center gap-2 mb-8" data-testid="review-stars">
                {[1,2,3,4,5].map((i) => (
                  <button key={i} type="button" data-testid={`review-star-${i}`} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)} className="transition-transform hover:scale-110">
                    <Star className={`w-9 h-9 ${i <= (hover || rating) ? "text-[#FFB800] fill-[#FFB800]" : "text-zinc-700"}`} />
                  </button>
                ))}
              </div>

              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("review.comment_label")}</label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="review-comment" placeholder={t("review.comment_ph")} className="bg-[#050505] border-white/10 rounded-none text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[110px] mb-6" />

              <button onClick={submit} disabled={sending} data-testid="review-submit" className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Star className="w-4 h-4" /> {t("review.submit")}</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
